/**
 * AdminLocales — the language registry.
 *
 * This is the screen the whole i18n design rests on: the locale list is DATA,
 * so commissioning a language is a row here plus a dictionary, with no enum to
 * edit and no rebuild. Everything else — the visitor's switcher, i18next's
 * fallback chain, the server-side content overlay, the lexicon export — reads
 * from this table.
 *
 * Deactivate rather than delete is the default action, and the UI says so:
 * deactivating hides a language from the switcher but keeps its translations,
 * while deleting takes them with it. That distinction matters most for the case
 * this page exists to handle — a language that was switched on before its
 * dictionary was ready.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Languages, Loader2, Plus, Save, Trash2, X, AlertTriangle } from 'lucide-react';
import {
  fetchAdminLocales,
  saveAdminLocale,
  deleteAdminLocale,
  type AdminLocale,
} from '../../services/adminLocalesApi';

const SOURCE = 'en';

/** A blank row for the "add a language" form. */
const EMPTY = {
  code: '',
  englishName: '',
  nativeName: '',
  fallbackCode: SOURCE as string | null,
  isActive: true,
  sortOrder: null as number | null,
};

export const AdminLocales: React.FC = () => {
  const [items, setItems] = useState<AdminLocale[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busyCode, setBusyCode] = useState<string | null>(null);

  const [editCode, setEditCode] = useState<string | null>(null);
  const [draft, setDraft] = useState({ ...EMPTY });
  const [adding, setAdding] = useState(false);
  const [newLocale, setNewLocale] = useState({ ...EMPTY });
  const [confirmDelete, setConfirmDelete] = useState<AdminLocale | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await fetchAdminLocales());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load locales');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const startEdit = (l: AdminLocale) => {
    setEditCode(l.code);
    setDraft({
      code: l.code,
      englishName: l.englishName,
      nativeName: l.nativeName,
      fallbackCode: l.fallbackCode,
      isActive: l.isActive,
      sortOrder: l.sortOrder,
    });
    setMessage(null);
  };

  const save = async (value: typeof EMPTY, isNew: boolean) => {
    setBusyCode(value.code || '__new__');
    setError(null);
    try {
      await saveAdminLocale(value);
      await reload();
      setEditCode(null);
      if (isNew) { setAdding(false); setNewLocale({ ...EMPTY }); }
      setMessage(
        isNew
          ? `Added ${value.code}. Drop dictionaries into public/locales/${value.code}/ or import a translated lexicon.`
          : `Saved ${value.code}.`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusyCode(null);
    }
  };

  /** Quick toggle from the table — the common action on this page. */
  const toggleActive = async (l: AdminLocale) => {
    await save({ ...l, isActive: !l.isActive }, false);
  };

  const doDelete = async (l: AdminLocale) => {
    setBusyCode(l.code);
    setError(null);
    try {
      const res = await deleteAdminLocale(l.code);
      await reload();
      setConfirmDelete(null);
      setMessage(
        res.translations > 0
          ? `Deleted ${l.code} and ${res.translations} translation${res.translations === 1 ? '' : 's'}.`
          : `Deleted ${l.code}.`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setBusyCode(null);
    }
  };

  /** Fallback options for a locale: every other locale, plus "none". */
  const fallbackOptions = (forCode: string) =>
    items.filter(l => l.code !== forCode).map(l => l.code);

  const renderForm = (
    value: typeof EMPTY,
    setValue: (v: typeof EMPTY) => void,
    isNew: boolean,
  ) => (
    <div className="row g-2 align-items-end">
      <div className="col-6 col-md-2">
        <label className="form-label small fw-semibold mb-1">Code</label>
        <input
          className="form-control form-control-sm"
          placeholder="es-MX"
          value={value.code}
          disabled={!isNew}
          onChange={e => setValue({ ...value, code: e.target.value.trim() })}
        />
      </div>
      <div className="col-6 col-md-3">
        <label className="form-label small fw-semibold mb-1">English name</label>
        <input
          className="form-control form-control-sm"
          placeholder="Spanish (Mexico)"
          value={value.englishName}
          onChange={e => setValue({ ...value, englishName: e.target.value })}
        />
      </div>
      <div className="col-6 col-md-2">
        <label className="form-label small fw-semibold mb-1">Native name</label>
        <input
          className="form-control form-control-sm"
          placeholder="Español"
          value={value.nativeName}
          onChange={e => setValue({ ...value, nativeName: e.target.value })}
        />
      </div>
      <div className="col-6 col-md-2">
        <label className="form-label small fw-semibold mb-1">Falls back to</label>
        <select
          className="form-select form-select-sm"
          value={value.fallbackCode ?? ''}
          disabled={value.code === SOURCE}
          onChange={e => setValue({ ...value, fallbackCode: e.target.value || null })}
        >
          <option value="">(none)</option>
          {fallbackOptions(value.code).map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div className="col-4 col-md-1">
        <label className="form-label small fw-semibold mb-1">Order</label>
        <input
          className="form-control form-control-sm"
          placeholder="—"
          value={value.sortOrder ?? ''}
          onChange={e => setValue({
            ...value,
            sortOrder: e.target.value.trim() === '' ? null : Number(e.target.value),
          })}
        />
      </div>
      <div className="col-8 col-md-2 d-flex gap-1">
        <button
          type="button"
          className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1"
          disabled={busyCode !== null || !value.code.trim() || !value.englishName.trim() || !value.nativeName.trim()}
          onClick={() => save(value, isNew)}
        >
          {busyCode === (value.code || '__new__')
            ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
            : <Save size={14} />}
          Save
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          disabled={busyCode !== null}
          onClick={() => { if (isNew) { setAdding(false); setNewLocale({ ...EMPTY }); } else setEditCode(null); }}
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );

  return (
    <div>
      <h2 className="fw-bold mb-1 d-flex align-items-center gap-2">
        <Languages size={20} /> Locales
      </h2>
      <p className="text-muted small mb-4">
        The languages this estimator offers. Adding one here makes it selectable; it will fall back
        to English for anything not yet translated. Supply its wording by dropping dictionaries into
        <code className="mx-1">public/locales/&lt;code&gt;/</code> or importing a translated lexicon
        under <strong>Translations</strong>.
      </p>

      {error && <div className="alert alert-danger py-2 small">{error}</div>}
      {message && <div className="alert alert-success py-2 small">{message}</div>}

      {loading ? (
        <div className="text-muted small d-flex align-items-center gap-2 py-3">
          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading…
        </div>
      ) : (
        <>
          <div className="card shadow-sm mb-3">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr className="small">
                    <th>Code</th>
                    <th>Name</th>
                    <th>Shown as</th>
                    <th>Falls back to</th>
                    <th className="text-center">Translations</th>
                    <th className="text-center">Offered</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(l => {
                    const isSource = l.code === SOURCE;
                    const busy = busyCode === l.code;
                    if (editCode === l.code) {
                      return (
                        <tr key={l.code}>
                          <td colSpan={7}>{renderForm(draft, setDraft, false)}</td>
                        </tr>
                      );
                    }
                    return (
                      <tr key={l.code}>
                        <td><code>{l.code}</code></td>
                        <td className="small">
                          {l.englishName}
                          {isSource && (
                            <span className="badge text-bg-secondary ms-2" style={{ fontSize: 10 }}>
                              source language
                            </span>
                          )}
                        </td>
                        <td className="small">{l.nativeName}</td>
                        <td className="small text-muted">{l.fallbackCode ?? '—'}</td>
                        <td className="text-center small">
                          {/* Zero is normal for a locale served entirely from
                              shipped dictionaries — this counts DB overrides. */}
                          <span className={l.translationCount > 0 ? 'fw-bold' : 'text-muted'}>
                            {l.translationCount}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="form-check form-switch d-inline-block m-0">
                            <input
                              type="checkbox"
                              role="switch"
                              className="form-check-input"
                              checked={l.isActive}
                              disabled={busy || isSource}
                              title={isSource ? 'English is always offered' : undefined}
                              onChange={() => toggleActive(l)}
                            />
                          </div>
                        </td>
                        <td className="text-end">
                          <button
                            className="btn btn-sm btn-outline-secondary me-1"
                            disabled={busy}
                            onClick={() => startEdit(l)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            disabled={busy || isSource}
                            title={isSource ? 'English cannot be deleted' : 'Delete this language'}
                            onClick={() => setConfirmDelete(l)}
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {adding ? (
            <div className="card shadow-sm">
              <div className="card-body">
                <h6 className="fw-bold mb-3">Add a language</h6>
                {renderForm(newLocale, setNewLocale, true)}
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
              onClick={() => { setAdding(true); setNewLocale({ ...EMPTY }); setMessage(null); }}
            >
              <Plus size={14} /> Add a language
            </button>
          )}
        </>
      )}

      {confirmDelete && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fw-bold d-flex align-items-center gap-2">
                  <AlertTriangle size={18} className="text-danger" />
                  Delete {confirmDelete.code}?
                </h5>
              </div>
              <div className="modal-body small">
                <p>
                  <strong>{confirmDelete.englishName}</strong> will stop being offered and
                  {confirmDelete.translationCount > 0 ? (
                    <> its <strong>{confirmDelete.translationCount}</strong> stored translation
                      {confirmDelete.translationCount === 1 ? '' : 's'} will be deleted.</>
                  ) : (
                    <> its registry entry will be removed.</>
                  )}
                </p>
                {/* The distinction that matters: deactivating solves the common
                    case (a language switched on too early) without data loss. */}
                <p className="text-muted mb-0">
                  To hide it from visitors without losing anything, switch <strong>Offered</strong>
                  off instead.
                </p>
              </div>
              <div className="modal-footer bg-light">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={busyCode !== null}
                  onClick={() => setConfirmDelete(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm d-inline-flex align-items-center gap-1"
                  disabled={busyCode !== null}
                  onClick={() => doDelete(confirmDelete)}
                >
                  {busyCode === confirmDelete.code
                    ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                    : <Trash2 size={14} />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLocales;
